import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Slide } from '@mui/material';
import { addDoc, collection } from 'firebase/firestore';
import { CheckIcon, QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Case } from '../../types';
import { logMeetingNotesAdded } from '../../utils/activityLogger';
import {
  buildSessionSelectOptions,
  defaultSessionForNewNote,
} from '../MeetingNotes/meetingNotesUtils';
import { t } from '../../utils/translations';
import Button from '../ui/Button';
import { SLIDE_TIMEOUT } from '../common/SlideUpTransition';

interface SessionQuestionsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Own active cases, for the quick note's target. */
  cases: Case[];
  sessionReportCounts: Record<string, number>;
  /** Opened from a case — that case is already the note's target. */
  preselectedCaseId?: string;
}

const ROWS = [
  { key: t.sessionQuestions.what, hint: t.sessionQuestions.whatHint },
  { key: t.sessionQuestions.how, hint: t.sessionQuestions.howHint },
  { key: t.sessionQuestions.why, hint: t.sessionQuestions.whyHint },
  { key: t.sessionQuestions.whereOften, hint: t.sessionQuestions.whereOftenHint },
  { key: t.sessionQuestions.when, hint: t.sessionQuestions.whenHint },
];

const FIELD =
  'mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-base text-slate-900 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 disabled:opacity-50 sm:text-sm';

/**
 * The question types a counselor is trained on, with somewhere to write underneath.
 *
 * The two halves belong together: a note taken while the questions are still on screen is the
 * one most likely to record what was actually asked rather than what the counselor remembers
 * asking. Splitting them — questions here, note behind another dialog — would lose exactly that.
 *
 * It lives in the top bar rather than on a case because the questions are identical before
 * every session; the note picks its case instead, so the aid is never gated behind one.
 */
const SessionQuestionsDialog: React.FC<SessionQuestionsDialogProps> = ({
  open,
  onClose,
  cases,
  sessionReportCounts,
  preselectedCaseId,
}) => {
  const { currentUser } = useAuth();
  const [caseId, setCaseId] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);

  const activeCases = useMemo(
    () =>
      cases
        .filter((c) => c.status === 'active')
        .sort((a, b) => a.counseledName.localeCompare(b.counseledName, 'ro')),
    [cases]
  );

  useEffect(() => {
    if (!open) return;
    setContent('');
    setSaved(false);
    setError(false);
    setCaseId((prev) => {
      if (preselectedCaseId && activeCases.some((c) => c.id === preselectedCaseId)) {
        return preselectedCaseId;
      }
      return activeCases.some((c) => c.id === prev) ? prev : '';
    });
  }, [open, activeCases, preselectedCaseId]);

  const handleSave = async () => {
    const target = activeCases.find((c) => c.id === caseId);
    if (!target || !content.trim() || saving) return;

    setSaving(true);
    setError(false);
    try {
      // Same document shape as the meeting-note form, so a note taken here is not a second
      // kind of note — it lands in the case's history like any other.
      const sessionNumber = defaultSessionForNewNote(
        buildSessionSelectOptions(sessionReportCounts[target.id] ?? 0, [])
      );
      await addDoc(collection(db, 'meetingNotes'), {
        caseId: target.id,
        content: content.trim(),
        sessionNumber,
        createdBy: currentUser?.id || '',
        createdByName: currentUser?.fullName || '',
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      if (currentUser) {
        await logMeetingNotesAdded(
          target.id,
          target.title,
          currentUser.id,
          currentUser.fullName || currentUser.email || 'Unknown User'
        );
      }
      setContent('');
      setSaved(true);
    } catch (err) {
      console.error('Error saving quick note:', err);
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  const canSave = Boolean(caseId) && Boolean(content.trim()) && !saving;

  /**
   * A Modal rather than a bare portal for two reasons: the top bar is `sticky` with a z-index,
   * so a dialog rendered inside it can never paint above the sidebar however high its own
   * z-index goes — and this opens straight out of the prepare-session dialog, which keeps
   * trapping focus on its way out unless a modal above it takes over.
   */
  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      aria-labelledby="session-questions-title"
      sx={{ zIndex: 1400 }}
      slotProps={{
        backdrop: {
          timeout: SLIDE_TIMEOUT,
          sx: { backgroundColor: 'rgba(15, 23, 42, 0.4)' },
        },
      }}
    >
      <Slide direction="up" in={open} timeout={SLIDE_TIMEOUT}>
        <div className="fixed inset-0 flex items-stretch justify-center outline-none sm:items-center sm:p-4">
          {/* Same box as "Pregătește sesiunea" on desktop: MUI's `sm` breakpoint (600px) and the
              shared `DIALOG_MAX_HEIGHT` (85vh) — two dialogs reading the same session's material
              should not visibly disagree on how much room that material gets. */}
          <div className="safe-pt safe-pb relative flex h-full w-full flex-col overflow-hidden bg-white shadow-lg sm:h-auto sm:max-h-[85vh] sm:max-w-[37.5rem] sm:rounded-xl sm:border sm:border-slate-200">
            <div className="flex shrink-0 items-start gap-3 border-b border-slate-100 px-4 pb-3 pt-4 sm:px-5 sm:pt-5">
              <div className="min-w-0 flex-1">
                <h2 id="session-questions-title" className="text-base font-semibold text-slate-900">
                  {t.sessionQuestions.title}
                </h2>
              </div>
              <button
                type="button"
                onClick={handleClose}
                aria-label={t.common.close}
                className="shrink-0 rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              <dl className="divide-y divide-slate-100">
                {ROWS.map((row) => (
                  <div key={row.key} className="flex gap-3 px-4 py-3 sm:px-5">
                    <dt className="w-[92px] shrink-0 text-sm font-semibold text-brand-600">
                      {row.key}
                    </dt>
                    <dd className="text-sm leading-5 text-slate-600">{row.hint}</dd>
                  </div>
                ))}
              </dl>

              <div className="flex items-start gap-2.5 border-y border-slate-100 bg-slate-50 px-4 py-3 sm:px-5">
                <QuestionMarkCircleIcon
                  className="mt-0.5 h-4 w-4 shrink-0 text-slate-400"
                  aria-hidden="true"
                />
                <div className="text-xs leading-[18px] text-slate-600">
                  <p className="font-semibold text-slate-700">{t.sessionQuestions.checkTitle}</p>
                  <p className="mt-1">{t.sessionQuestions.presupus}</p>
                  <p className="mt-1">{t.sessionQuestions.intrebareLipsa}</p>
                </div>
              </div>

              <div className="px-4 py-4 sm:px-5">
                <h3 className="text-sm font-semibold text-slate-900">
                  {t.sessionQuestions.noteTitle}
                </h3>
                {activeCases.length === 0 ? (
                  <p className="mt-3 text-sm text-slate-500">{t.sessionQuestions.noCases}</p>
                ) : (
                  <>
                    <label className="mt-3 block text-sm font-medium text-slate-700">
                      {t.sessionQuestions.noteCaseLabel}
                      <select
                        value={caseId}
                        onChange={(e) => {
                          setCaseId(e.target.value);
                          setSaved(false);
                        }}
                        disabled={saving}
                        className={FIELD}
                      >
                        <option value="">{t.sessionReports.selectCaseForNote}</option>
                        {activeCases.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.counseledName} · {c.title}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="mt-3 block text-sm font-medium text-slate-700">
                      {t.meetingNotes.addNote}
                      <textarea
                        value={content}
                        onChange={(e) => {
                          setContent(e.target.value);
                          setSaved(false);
                        }}
                        disabled={saving}
                        rows={4}
                        placeholder={t.sessionQuestions.notePlaceholder}
                        className={`${FIELD} resize-y`}
                      />
                    </label>

                    {error && (
                      <p className="mt-2 text-xs text-red-600">{t.sessionQuestions.noteError}</p>
                    )}
                  </>
                )}
              </div>
            </div>

            {activeCases.length > 0 && (
              <div className="flex shrink-0 items-center gap-2 border-t border-slate-100 px-4 py-3 sm:px-5">
                {saved && (
                  <span className="flex items-center gap-1.5 text-xs font-medium text-green-700">
                    <CheckIcon className="h-4 w-4" aria-hidden="true" />
                    {t.sessionQuestions.noteSaved}
                  </span>
                )}
                <div className="ml-auto">
                  <Button
                    variant="primary"
                    onClick={() => void handleSave()}
                    disabled={!canSave}
                    loading={saving}
                  >
                    {t.sessionQuestions.noteSave}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Slide>
    </Modal>
  );
};

export default SessionQuestionsDialog;
